import './ibm-products-config';
import { EditInPlace } from '@carbon/ibm-products';
import { useLayoutEffect, type ComponentProps } from 'react';
import classNames from 'classnames';

import styles from './inline-edit.module.scss';

type InlineEditProps = ComponentProps<typeof EditInPlace>;

// Password managers read the bare text input as a login field and fill it —
// LastPass drops the account username straight into the title. EditInPlace
// builds the input with a fixed prop list and spreads the rest onto its wrapper
// div, so the opt-outs cannot be passed as props and have to be set on the
// element itself. Each vendor reads its own attribute; `autocomplete` alone is
// widely ignored by them.
const OPT_OUT_ATTRIBUTES: [string, string][] = [
  ['autocomplete', 'off'],
  ['data-lpignore', 'true'],
  ['data-1p-ignore', ''],
  ['data-bwignore', 'true'],
  ['data-form-type', 'other'],
];

export const InlineEdit = ({ className, ...props }: InlineEditProps) => {
  const { id } = props;

  useLayoutEffect(() => {
    const input = document.getElementById(id);
    if (!input) return;

    OPT_OUT_ATTRIBUTES.forEach(([name, value]) => {
      input.setAttribute(name, value);
    });

    // Password managers only offer credentials on fields they read as a login
    // input, and they skip search fields. The type is set here rather than
    // passed down because EditInPlace hard-codes `type="text"` on the input.
    input.setAttribute('type', 'search');

    // The opt-outs above only suppress the vendors' inline icons — LastPass
    // still fills the field from its page-load pass, which skips read-only
    // inputs. The field is read-only until the user focuses it to edit, so by
    // the time it can be filled the fill pass has already run.
    input.setAttribute('readonly', '');
    const unlock = () => input.removeAttribute('readonly');
    const lock = () => input.setAttribute('readonly', '');

    input.addEventListener('focus', unlock);
    input.addEventListener('blur', lock);

    return () => {
      input.removeEventListener('focus', unlock);
      input.removeEventListener('blur', lock);
    };
  }, [id]);

  return (
    <EditInPlace
      {...props}
      className={classNames(styles.inlineEdit, className)}
    />
  );
};
